import api from './api';

/**
 * XLSX Data Extractor API Service
 * Provides functions to interact with the XLSX extractor endpoints
 */

/**
 * Extract data from an uploaded XLSX file
 * @param {File} file - The XLSX file to upload and extract data from
 * @returns {Promise<Object>} The extraction results
 */
export const extractXlsxData = async (file) => {
  try {
    const formData = new FormData();
    formData.append('xlsxFile', file);

    const response = await api.post('/v4/xlsx-extractor/extract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error extracting XLSX data:', error);
    throw error;
  }
};

/**
 * Debug XLSX file structure to see what sheets are detected
 * @param {File} file - The XLSX file to debug
 * @returns {Promise<Object>} The file structure information
 */
export const debugXlsxFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('xlsxFile', file);

    const response = await api.post('/v4/xlsx-extractor/debug', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error debugging XLSX file:', error);
    throw error;
  }
};

/**
 * Test file upload functionality
 * @param {File} file - The XLSX file to test upload
 * @returns {Promise<Object>} The test results
 */
export const testFileUpload = async (file) => {
  try {
    const formData = new FormData();
    formData.append('xlsxFile', file);

    const response = await api.post('/v4/xlsx-extractor/test-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error testing file upload:', error);
    throw error;
  }
};

/**
 * Get database status and item cell counts
 * @returns {Promise<Object>} The database status information
 */
export const getDatabaseStatus = async () => {
  try {
    const response = await api.get('/v4/xlsx-extractor/database-status');
    return response.data;
  } catch (error) {
    console.error('Error fetching database status:', error);
    throw error;
  }
};

/**
 * Get all item cells information from the database (for debugging/reference)
 * @returns {Promise<Object>} The item cells data
 */
export const getItemCells = async () => {
  try {
    const response = await api.get('/v4/xlsx-extractor/item-cells');
    return response.data;
  } catch (error) {
    console.error('Error fetching item cells:', error);
    throw error;
  }
};

/**
 * Validate if a file is a valid XLSX file
 * @param {File} file - The file to validate
 * @returns {boolean} True if valid XLSX file
 */
export const validateXlsxFile = (file) => {
  if (!file) return false;
  
  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];
  
  const allowedExtensions = ['.xlsx', '.xls'];
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  
  return allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 